import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { ticketsApi } from '../../../../services/apiClient';

// Components
import TicketHeader from './TicketHeader';
import TicketContent from './TicketContent';
import TicketSidebar from './TicketSidebar';
import MessagesTab from './MessagesTab';
import AttachmentsTab from './AttachmentsTab';
import ActivityTab from './ActivityTab';
import { TicketDetailSkeleton } from '../shared/TicketSkeleton';

// Dialogs
import {
  InterventionDialog,
  RescheduleDialog,
  CancelInterventionDialog,
  InterventionSelectionDialog
} from '../dialogs';

// Existing ticket dialogs
import { SaveAsKnownIssue } from '../../../../components/Tickets/SaveAsKnownIssue';
import { SelectExistingSolution } from '../../../../components/Tickets/SelectExistingSolution';

// Hooks
import { useInterventions } from '../../hooks/useInterventions';

// Utils
import { transformTicketData } from '../../utils/ticketTransformers';

/**
 * Tab panel component
 */
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`ticket-tabpanel-${index}`}
    aria-labelledby={`ticket-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

/**
 * Main ticket detail view container
 */
const TicketDetailView = ({
  ticket,
  loading,
  onBack,
  onEdit,
  onDelete,
  onUpdate
}) => {
  const { t } = useThemeSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [tabValue, setTabValue] = useState(0);
  const [messages, setMessages] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [loadingFollow, setLoadingFollow] = useState(false);

  // Intervention state from hook
  const {
    linkedInterventions,
    linkedIntervention,
    sending: sendingIntervention,
    rescheduling: reschedulingIntervention,
    cancelling: cancellingIntervention,
    fetchInterventions,
    requestIntervention,
    rescheduleIntervention,
    cancelIntervention
  } = useInterventions(ticket?.id);

  // Dialogs state
  const [saveAsKnownIssueOpen, setSaveAsKnownIssueOpen] = useState(false);
  const [selectSolutionOpen, setSelectSolutionOpen] = useState(false);
  const [interventionDialogOpen, setInterventionDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [interventionSelectionOpen, setInterventionSelectionOpen] = useState(false);

  // Check if user is support staff
  const isSupport = user?.role === 'admin' || user?.role === 'support' || user?.companyId === 1;

  // Fetch messages when ticket changes
  useEffect(() => {
    if (ticket?.id) {
      fetchMessages();
      checkFollowStatus();
    }
  }, [ticket?.id]);

  const fetchMessages = async () => {
    try {
      const response = await ticketsApi.getById(ticket.id);
      const ticketData = response.data?.data?.ticket || response.data?.ticket || response.data;
      if (ticketData?.TicketMessages) {
        setMessages([...ticketData.TicketMessages].reverse());
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const checkFollowStatus = async () => {
    try {
      const response = await ticketsApi.getFollowers(ticket.id);
      const followers = response.data?.followers || [];
      setFollowersCount(followers.length);
      setIsFollowing(followers.some(f => f.userId === user?.id));
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  // Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleToggleFollow = async () => {
    setLoadingFollow(true);
    try {
      if (isFollowing) {
        await ticketsApi.unfollow(ticket.id);
        setIsFollowing(false);
        setFollowersCount(prev => Math.max(0, prev - 1));
        enqueueSnackbar('Unfollowed ticket', { variant: 'success' });
      } else {
        await ticketsApi.follow(ticket.id);
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
        enqueueSnackbar('Following ticket', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      enqueueSnackbar('Failed to update follow status', { variant: 'error' });
    } finally {
      setLoadingFollow(false);
    }
  };

  // Re-fetch the full ticket after any update to prevent data loss from partial API responses
  const refreshTicketAfterUpdate = async () => {
    const response = await ticketsApi.getById(ticket.id);
    const ticketData = response.data?.data?.ticket || response.data?.ticket || response.data;
    return transformTicketData(ticketData);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await ticketsApi.update(ticket.id, { status: newStatus });
      const updatedTicket = await refreshTicketAfterUpdate();
      onUpdate(updatedTicket);
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    }
  };

  const handleUrgencyChange = async (newUrgency) => {
    try {
      await ticketsApi.update(ticket.id, { urgency: newUrgency });
      const updatedTicket = await refreshTicketAfterUpdate();
      onUpdate(updatedTicket);
      enqueueSnackbar('Urgency updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating urgency:', error);
      enqueueSnackbar('Failed to update urgency', { variant: 'error' });
    }
  };

  const handleLocationChange = async (locationId, locationName) => {
    try {
      await ticketsApi.update(ticket.id, { location_id: locationId });
      const updatedTicket = await refreshTicketAfterUpdate();
      onUpdate(updatedTicket);
      enqueueSnackbar(t ? t('Tickets.location_updated') : 'Location updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating location:', error);
      enqueueSnackbar(t ? t('Tickets.location_update_failed') : 'Failed to update location', { variant: 'error' });
    }
  };

  const handleDeviceChange = async (deviceId, deviceName) => {
    try {
      await ticketsApi.update(ticket.id, {
        related_device_id: deviceId,
        related_device_name: deviceName
      });
      const updatedTicket = await refreshTicketAfterUpdate();
      onUpdate(updatedTicket);
      enqueueSnackbar(t ? t('Tickets.device_updated') : 'Device updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating device:', error);
      enqueueSnackbar(t ? t('Tickets.device_update_failed') : 'Failed to update device', { variant: 'error' });
    }
  };

  const handleNextStepChange = async (nextStep) => {
    try {
      await ticketsApi.update(ticket.id, { next_step: nextStep });
      const updatedTicket = await refreshTicketAfterUpdate();
      onUpdate(updatedTicket);
      enqueueSnackbar(t ? t('Tickets.next_step_updated') : 'Next step updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating next step:', error);
      enqueueSnackbar(t ? t('Tickets.next_step_update_failed') : 'Failed to update next step', { variant: 'error' });
    }
  };

  const handleSupportTypeChange = async (newSupportType) => {
    try {
      await ticketsApi.update(ticket.id, { support_type: newSupportType });
      const updatedTicket = await refreshTicketAfterUpdate();
      onUpdate(updatedTicket);
      enqueueSnackbar(t ? t('Tickets.support_type_updated') : 'Support type updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating support type:', error);
      enqueueSnackbar(t ? t('Tickets.support_type_update_failed') : 'Failed to update support type', { variant: 'error' });
    }
  };

  const handleMarkResolved = async () => {
    try {
      await ticketsApi.update(ticket.id, { status: 'Closed', next_step: 'Resolved' });
      const updatedTicket = await refreshTicketAfterUpdate();
      onUpdate(updatedTicket);
      enqueueSnackbar(t ? t('Tickets.marked_as_resolved') : 'Ticket marked as resolved', { variant: 'success' });
    } catch (error) {
      console.error('Error marking ticket as resolved:', error);
      enqueueSnackbar(t ? t('Tickets.mark_resolved_failed') : 'Failed to mark ticket as resolved', { variant: 'error' });
    }
  };

  const handleReopen = async () => {
    try {
      await ticketsApi.update(ticket.id, { status: 'In Progress', next_step: 'Reopened' });
      const updatedTicket = await refreshTicketAfterUpdate();
      onUpdate(updatedTicket);
      enqueueSnackbar(t ? t('Tickets.reopened') : 'Ticket reopened successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error reopening ticket:', error);
      enqueueSnackbar(t ? t('Tickets.reopen_failed') : 'Failed to reopen ticket', { variant: 'error' });
    }
  };

  const handleSendMessage = async (message, files) => {
    try {
      await ticketsApi.addComment(ticket.id, { message });
      await fetchMessages();
      enqueueSnackbar('Message sent', { variant: 'success' });
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };

  const handleFilesUploaded = async (uploadedFiles) => {
    try {
      for (const file of uploadedFiles) {
        await ticketsApi.addAttachment(ticket.id, {
          file_id: file.file_id,
          originalName: file.name,
          size: file.size,
          mimetype: file.mime_type,
          url: file.url
        });
      }
      enqueueSnackbar('Attachments added successfully', { variant: 'success' });

      // Refresh ticket
      const response = await ticketsApi.getById(ticket.id);
      const updatedTicket = transformTicketData(
        response.data?.data?.ticket || response.data?.ticket || response.data
      );
      onUpdate(updatedTicket);
    } catch (error) {
      console.error('Error adding attachments:', error);
      enqueueSnackbar('Failed to add attachments', { variant: 'error' });
    }
  };

  const handleViewReport = () => {
    if (linkedInterventions.length > 1) {
      // Show selection dialog
      setInterventionSelectionOpen(true);
    } else if (linkedIntervention) {
      navigate(`/field-interventions/${linkedIntervention.id}`);
    }
  };

  const handleInterventionSelect = (intervention) => {
    setInterventionSelectionOpen(false);
    navigate(`/field-interventions/${intervention.id}`);
  };

  // Intervention handlers (delegate to useInterventions hook)
  const handleRequestIntervention = async (data) => {
    const result = await requestIntervention(data);
    if (result.success) {
      enqueueSnackbar(t ? t('Tickets.intervention_request_sent') : 'Intervention request sent successfully', { variant: 'success' });
      setInterventionDialogOpen(false);
    } else {
      enqueueSnackbar(result.error || (t ? t('Tickets.intervention_request_failed') : 'Failed to request intervention'), { variant: 'error' });
    }
  };

  const handleRescheduleIntervention = async ({ date, time, notes }) => {
    if (!linkedIntervention) return;
    const result = await rescheduleIntervention(linkedIntervention.id, { date, time, notes });
    if (result.success) {
      enqueueSnackbar(t ? t('Tickets.intervention_rescheduled') : 'Intervention rescheduled successfully', { variant: 'success' });
      setRescheduleDialogOpen(false);
    } else {
      enqueueSnackbar(result.error || (t ? t('Tickets.intervention_reschedule_failed') : 'Failed to reschedule intervention'), { variant: 'error' });
    }
  };

  const handleCancelIntervention = async (reason) => {
    if (!linkedIntervention) return;
    const result = await cancelIntervention(linkedIntervention.id, reason);
    if (result.success) {
      enqueueSnackbar(t ? t('Tickets.intervention_cancelled') : 'Intervention cancelled successfully', { variant: 'success' });
      setCancelDialogOpen(false);
    } else {
      enqueueSnackbar(result.error || (t ? t('Tickets.intervention_cancel_failed') : 'Failed to cancel intervention'), { variant: 'error' });
    }
  };

  if (loading || !ticket) {
    return <TicketDetailSkeleton />;
  }

  // Count standalone attachments for tab badge
  const standaloneAttachmentCount = ticket?.TicketAttachments?.filter(
    a => !a.messageId && !a.message_id
  ).length || 0;

  return (
    <Box>
      {/* Header */}
      <TicketHeader
        ticket={ticket}
        isSupport={isSupport}
        isFollowing={isFollowing}
        followersCount={followersCount}
        loadingFollow={loadingFollow}
        linkedIntervention={linkedIntervention}
        linkedInterventions={linkedInterventions}
        onBack={onBack}
        onEdit={onEdit}
        onDelete={onDelete}
        onSaveAsKnownIssue={() => setSaveAsKnownIssueOpen(true)}
        onApplySolution={() => setSelectSolutionOpen(true)}
        onToggleFollow={handleToggleFollow}
        onViewReport={handleViewReport}
        onReschedule={() => setRescheduleDialogOpen(true)}
        onCancelIntervention={() => setCancelDialogOpen(true)}
        onRequestIntervention={() => setInterventionDialogOpen(true)}
        onMarkResolved={handleMarkResolved}
        onReopen={handleReopen}
      />

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={7} lg={8}>
          {/* Description & Steps */}
          <TicketContent
            ticket={ticket}
            isSupport={isSupport}
            onSolutionSelect={(solution) => {
              console.log('Selected solution:', solution);
            }}
          />

          {/* Tabs */}
          <Paper sx={{ p: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={t ? t("Tickets.messages") : "Messages"} />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{t ? t("SaveAsKnownIssue.attachments") : "Attachments"}</span>
                    {standaloneAttachmentCount > 0 && (
                      <Chip
                        label={standaloneAttachmentCount}
                        size="small"
                        color="primary"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab label={t ? t("Tickets.activity") : "Activity"} />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <MessagesTab
                messages={messages}
                ticketId={ticket.id}
                interventionDate={ticket.intervention_date}
                onSendMessage={handleSendMessage}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <AttachmentsTab
                ticket={ticket}
                linkedInterventions={linkedInterventions}
                onFilesUploaded={handleFilesUploaded}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <ActivityTab ticket={ticket} />
            </TabPanel>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={5} lg={4}>
          <TicketSidebar
            ticket={ticket}
            isSupport={isSupport}
            onStatusChange={handleStatusChange}
            onUrgencyChange={handleUrgencyChange}
            onLocationChange={handleLocationChange}
            onDeviceChange={handleDeviceChange}
            onNextStepChange={handleNextStepChange}
            onSupportTypeChange={handleSupportTypeChange}
          />
        </Grid>
      </Grid>

      {/* Dialogs */}

      {/* Save as Known Issue Dialog */}
      {isSupport && (
        <SaveAsKnownIssue
          ticket={ticket}
          open={saveAsKnownIssueOpen}
          onClose={() => setSaveAsKnownIssueOpen(false)}
          onSaved={(issueId) => {
            setSaveAsKnownIssueOpen(false);
            enqueueSnackbar('Saved as known issue', { variant: 'success' });
          }}
        />
      )}

      {/* Select Existing Solution Dialog */}
      {isSupport && (
        <SelectExistingSolution
          ticketId={ticket?.id}
          open={selectSolutionOpen}
          onClose={() => setSelectSolutionOpen(false)}
          onApplied={() => {
            setSelectSolutionOpen(false);
            enqueueSnackbar('Solution applied successfully', { variant: 'success' });
          }}
        />
      )}

      {/* Intervention Request Dialog */}
      <InterventionDialog
        open={interventionDialogOpen}
        onClose={() => setInterventionDialogOpen(false)}
        onSubmit={handleRequestIntervention}
        ticket={ticket}
        sending={sendingIntervention}
      />

      {/* Reschedule Intervention Dialog */}
      <RescheduleDialog
        open={rescheduleDialogOpen}
        onClose={() => setRescheduleDialogOpen(false)}
        onSubmit={handleRescheduleIntervention}
        intervention={linkedIntervention}
        rescheduling={reschedulingIntervention}
      />

      {/* Cancel Intervention Dialog */}
      <CancelInterventionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onSubmit={handleCancelIntervention}
        cancelling={cancellingIntervention}
      />

      {/* Intervention Selection Dialog */}
      <InterventionSelectionDialog
        open={interventionSelectionOpen}
        onClose={() => setInterventionSelectionOpen(false)}
        onSelect={handleInterventionSelect}
        interventions={linkedInterventions}
      />
    </Box>
  );
};

export default TicketDetailView;
